import React, { useEffect, useRef, useState, useCallback } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Volume2, VolumeX, X, Coins, Heart, Sparkles } from "lucide-react";
import { useApp } from "../../context/AppContext";
import { pickRandomLevels, HEROES } from "./data/cashStrikeContent";
import {
    primeAudio, isMuted, setMuted,
    sfxCannon, sfxSplash, sfxCoin, sfxWrong, sfxGameOver,
    sfxPowerup, sfxStreak, sfxLevelUp, sfxCashRegister,
    startAmbient, stopAmbient,
} from "./cashStrikeAudio";

const BACKEND = process.env.REACT_APP_BACKEND_URL || "";
const heroUrl = (id) => `${BACKEND}/api/arcade/heroes/${id}.png`;
const heroFullUrl = (id) => `${BACKEND}/api/arcade/heroes/${id}_full.png`;
const BACKDROP_URL = `${BACKEND}/api/arcade/heroes/_backdrop.png`;

// Per-hero gameplay perks. Each hero picks a different power fantasy so the
// character-select feels meaningful.
const PERKS = {
    penny: { fallMul: 0.75,  streakAt: 3, coinMul: 1.0,  bulletMul: 1.0, buckBonus: false, label_en: "Slower crates",       label_es: "Cajas más lentas" },
    tessa: { fallMul: 1.0,   streakAt: 3, coinMul: 1.0,  bulletMul: 1.25, buckBonus: false, label_en: "Quick-draw bullets",  label_es: "Balas rápidas" },
    mia:   { fallMul: 1.0,   streakAt: 3, coinMul: 1.25, bulletMul: 1.0, buckBonus: false, label_en: "+25% coins",           label_es: "+25% monedas" },
    cash:  { fallMul: 1.0,   streakAt: 3, coinMul: 1.0,  bulletMul: 1.4, buckBonus: false, label_en: "Rocket-fast shots",    label_es: "Disparos veloces" },
    buck:  { fallMul: 1.0,   streakAt: 3, coinMul: 1.0,  bulletMul: 1.0, buckBonus: true,  label_en: "Splash damage",        label_es: "Daño por área" },
    finn:  { fallMul: 1.0,   streakAt: 2, coinMul: 1.0,  bulletMul: 1.0, buckBonus: false, label_en: "Streak at ×2",         label_es: "Racha desde ×2" },
};
const getPerk = (id) => PERKS[id] || PERKS.penny;

// Powerup types (dropped by rare golden crates from level 3+).
const POWERUPS = ["freeze", "triple", "life"];
const powerupLabel = (kind, isEs) => ({
    freeze: isEs ? "Congelar" : "Freeze",
    triple: isEs ? "Triple Tiro" : "Triple Shot",
    life:   isEs ? "Vida Extra" : "Extra Life",
}[kind]);

/**
 * CA$H STRIKE — the beach cannon shooter.
 *  - Cannon at bottom aims toward mouse / touch, click / tap fires a water bullet.
 *  - Wooden crates drift down under parachutes carrying possible answers.
 *  - Right crate → coins + explanation. Wrong crate → lose a life + explanation.
 *  - 3 lives, level advances on each correct answer.
 *  - Beach illustrated hero avatars, procedural audio, mute toggle.
 */
const CashStrike = ({ onExit }) => {
    const { language } = useApp();
    const isEs = language === "es";

    // Question set is drawn fresh at every new run — pickRandomLevels shuffles
    // the ~100-strong bank so no two playthroughs use the same order.
    const [levels, setLevels] = useState(() => pickRandomLevels(12, language));
    const LEVELS = levels;

    const [hero, setHero] = useState(null);          // selected hero id
    const [phase, setPhase] = useState("select");    // 'select' | 'playing' | 'feedback' | 'over' | 'win'
    const [lives, setLives] = useState(3);
    const [coins, setCoins] = useState(0);
    const [levelIdx, setLevelIdx] = useState(0);
    const [feedback, setFeedback] = useState(null);  // { correct, why }
    const [muted, setMutedState] = useState(false);
    const [levelBanner, setLevelBanner] = useState(false);
    const [streak, setStreak] = useState(0);
    const [streakBanner, setStreakBanner] = useState(0);  // last streak count that popped a banner
    const [powerupHUD, setPowerupHUD] = useState(null);   // { kind, expiresAt } for on-screen chip
    const canvasRef = useRef(null);
    const containerRef = useRef(null);
    const backdropImgRef = useRef(null);
    const heroImgRef = useRef(null);
    const heroFullRef = useRef(null);
    const stateRef = useRef({
        aim: { x: 0.5, y: 0.3 },
        heroX: 0.5,
        crates: [],
        bullets: [],
        coinsFx: [],
        splashFx: [],                  // water droplets on impact
        rafId: null,
        canvasW: 0, canvasH: 0,
        acceptingInput: true,
        freezeUntil: 0,                // performance.now() ms — crates paused while > now
        tripleShotsLeft: 0,            // remaining fan-shot bullets
        perk: PERKS.penny,             // resolved perk for the active hero
    });

    /* ---------- Preload backdrop image once ---------- */
    useEffect(() => {
        const img = new Image();
        img.crossOrigin = "anonymous";
        img.onload = () => { backdropImgRef.current = img; };
        img.src = BACKDROP_URL;
    }, []);

    /* ---------- Preload selected hero portrait AND full-body sprite ---------- */
    useEffect(() => {
        if (!hero) return;
        // Portrait fallback (still used in feedback overlay if we want it later)
        const p = new Image();
        p.crossOrigin = "anonymous";
        p.onload = () => { heroImgRef.current = p; };
        p.src = heroUrl(hero);
        // Full-body sprite — this is what's actually rendered on the game canvas.
        const f = new Image();
        f.crossOrigin = "anonymous";
        f.onload = () => { heroFullRef.current = f; };
        f.src = heroFullUrl(hero);
    }, [hero]);

    /* ---------- Spawn a fresh level of STATIC crates in a row at the top ---------- */
    const spawnLevel = useCallback((idx) => {
        const lvl = LEVELS[idx];
        if (!lvl) return;
        const opts = [...lvl.options];
        // shuffle
        for (let i = opts.length - 1; i > 0; i--) {
            const j = Math.floor(Math.random() * (i + 1));
            [opts[i], opts[j]] = [opts[j], opts[i]];
        }
        const W = stateRef.current.canvasW || 800;
        const H = stateRef.current.canvasH || 600;
        const n = opts.length;
        // Crates stay static in a horizontal row below the question banner.
        // Size scales with viewport but stays slightly smaller so the beach breathes.
        const crateW = Math.max(130, Math.min(200, W * 0.21));
        const crateH = Math.max(105, Math.min(150, crateW * 0.72));
        const gap = crateW * 1.32;
        const totalW = gap * (n - 1);
        const startX = W / 2 - totalW / 2;
        // Center Y sits well below the top question banner (~H*0.18) and above the hero.
        const cy = Math.max(H * 0.34, 200);
        const baseCrates = opts.map((o, i) => ({
            id: `${idx}-${i}-${Math.random().toString(36).slice(2, 6)}`,
            x: startX + i * gap,
            y: cy,
            text: o.text,
            correct: o.correct,
            why: o.why,
            w: crateW,
            h: crateH,
            opened: false,
            correctOpen: false,
            wrongOpen: false,
            shake: 0,
            hovered: false,
            powerup: null,
        }));

        stateRef.current.crates = baseCrates;
        stateRef.current.bullets = [];
        stateRef.current.acceptingInput = true;
    }, [LEVELS]);

    /* ---------- Draw loop ---------- */
    const draw = useCallback((dt) => {
        const canvas = canvasRef.current;
        if (!canvas) return;
        const ctx2d = canvas.getContext("2d");
        const dpr = window.devicePixelRatio || 1;
        const W = canvas.clientWidth;
        const H = canvas.clientHeight;
        if (canvas.width !== W * dpr) {
            canvas.width = W * dpr;
            canvas.height = H * dpr;
        }
        ctx2d.setTransform(dpr, 0, 0, dpr, 0, 0);
        stateRef.current.canvasW = W;
        stateRef.current.canvasH = H;

        // ---- backdrop (illustrated beach scene) with graceful gradient fallback ----
        const bg = backdropImgRef.current;
        if (bg && bg.complete && bg.naturalWidth > 0) {
            // "cover" fit so the beach always fills the canvas without stretching
            const iw = bg.naturalWidth, ih = bg.naturalHeight;
            const cr = W / H, ir = iw / ih;
            let sx = 0, sy = 0, sw = iw, sh = ih;
            if (ir > cr) { // image wider than canvas → crop sides
                sw = ih * cr;
                sx = (iw - sw) / 2;
            } else {       // image taller → crop top/bottom (bias downward to keep the beach visible)
                sh = iw / cr;
                sy = (ih - sh) * 0.55;
            }
            ctx2d.drawImage(bg, sx, sy, sw, sh, 0, 0, W, H);
        } else {
            // Fallback gradient while backdrop is still loading
            const sky = ctx2d.createLinearGradient(0, 0, 0, H);
            sky.addColorStop(0, "#78D9F5");
            sky.addColorStop(0.55, "#B7E7F7");
            sky.addColorStop(0.75, "#F6E5B9");
            sky.addColorStop(1, "#F0C583");
            ctx2d.fillStyle = sky;
            ctx2d.fillRect(0, 0, W, H);
            const sand = ctx2d.createLinearGradient(0, H * 0.78, 0, H);
            sand.addColorStop(0, "#F5DFA4");
            sand.addColorStop(1, "#E9C671");
            ctx2d.fillStyle = sand;
            ctx2d.fillRect(0, H * 0.78, W, H * 0.22);
        }

        // subtle vignette so HUD chips pop
        const vig = ctx2d.createRadialGradient(W / 2, H * 0.55, H * 0.35, W / 2, H * 0.5, H * 0.85);
        vig.addColorStop(0, "rgba(0,0,0,0)");
        vig.addColorStop(1, "rgba(0,0,0,0.28)");
        ctx2d.fillStyle = vig;
        ctx2d.fillRect(0, 0, W, H);

        // Hercules plane silhouette drifting slowly
        const t = performance.now() / 1000;
        const px = ((t * 22) % (W + 240)) - 120;
        ctx2d.save();
        ctx2d.translate(px, H * 0.09);
        ctx2d.fillStyle = "rgba(35, 55, 70, 0.85)";
        // fuselage
        ctx2d.beginPath();
        ctx2d.ellipse(0, 0, 42, 8, 0, 0, Math.PI * 2);
        ctx2d.fill();
        // wings
        ctx2d.fillRect(-24, -6, 48, 3);
        ctx2d.fillRect(-30, 1, 60, 3);
        // tail
        ctx2d.beginPath();
        ctx2d.moveTo(28, 0); ctx2d.lineTo(46, -12); ctx2d.lineTo(46, 4); ctx2d.closePath();
        ctx2d.fill();
        ctx2d.restore();

        // ---- crates: STATIC row at top. Gentle horizontal sway on ropes.
        stateRef.current.crates.forEach((c) => {
            const sx = Math.sin((performance.now() / 700) + c.x) * 4;
            const cx = c.x + sx;
            const cy = c.y;

            // parachute REMOVED — crates now drift horizontally / hang like carnival
            // targets. No overhead canopy drawn.
            if (false && !c.opened) {}

            // crate body
            const jitter = c.shake > 0 ? (Math.random() - 0.5) * c.shake : 0;
            ctx2d.save();
            ctx2d.translate(cx + jitter, cy);

            // hard drop shadow
            ctx2d.fillStyle = "rgba(0,0,0,0.28)";
            ctx2d.fillRect(-c.w / 2 + 5, -c.h / 2 + 8, c.w, c.h);

            if (c.wrongOpen) {
                // WRONG SHOT — box splits open showing empty dark interior
                // Left half
                ctx2d.fillStyle = "#5A3B1E";
                ctx2d.beginPath();
                ctx2d.moveTo(-c.w / 2, -c.h / 2);
                ctx2d.lineTo(-4, -c.h / 2 - 8);
                ctx2d.lineTo(-4, c.h / 2);
                ctx2d.lineTo(-c.w / 2, c.h / 2);
                ctx2d.closePath();
                ctx2d.fill();
                // Right half
                ctx2d.beginPath();
                ctx2d.moveTo(4, -c.h / 2 - 8);
                ctx2d.lineTo(c.w / 2, -c.h / 2);
                ctx2d.lineTo(c.w / 2, c.h / 2);
                ctx2d.lineTo(4, c.h / 2);
                ctx2d.closePath();
                ctx2d.fill();
                // Empty inside
                ctx2d.fillStyle = "#1A0F06";
                ctx2d.fillRect(-4, -c.h / 2, 8, c.h);
                // EMPTY label
                ctx2d.fillStyle = "#8B6A44";
                ctx2d.font = "bold 12px ui-sans-serif, system-ui";
                ctx2d.textAlign = "center";
                ctx2d.textBaseline = "middle";
                ctx2d.fillText(isEs ? "VACÍO" : "EMPTY", 0, 0);
            } else {
                // wood body — gold for powerup crates, gold on correct hit, wood otherwise
                const grad = ctx2d.createLinearGradient(0, -c.h / 2, 0, c.h / 2);
                if (c.correctOpen) {
                    grad.addColorStop(0, "#F6D24B");
                    grad.addColorStop(1, "#B8942F");
                } else if (c.powerup) {
                    grad.addColorStop(0, "#FCE38A");
                    grad.addColorStop(0.5, "#F6D24B");
                    grad.addColorStop(1, "#B8842A");
                } else {
                    grad.addColorStop(0, "#C08B4E");
                    grad.addColorStop(1, "#7C5225");
                }
                ctx2d.fillStyle = grad;
                ctx2d.fillRect(-c.w / 2, -c.h / 2, c.w, c.h);

                // golden pulsing outline on powerup crates
                if (c.powerup && !c.opened) {
                    const puls = 0.6 + 0.4 * Math.sin(performance.now() / 180);
                    ctx2d.strokeStyle = `rgba(255, 235, 130, ${puls})`;
                    ctx2d.lineWidth = 4;
                    ctx2d.strokeRect(-c.w / 2 - 2, -c.h / 2 - 2, c.w + 4, c.h + 4);
                }

                // plank lines (wood grain)
                ctx2d.strokeStyle = "rgba(0,0,0,0.32)";
                ctx2d.lineWidth = 1.5;
                for (let i = 1; i < 3; i++) {
                    const yy = -c.h / 2 + (c.h / 3) * i;
                    ctx2d.beginPath();
                    ctx2d.moveTo(-c.w / 2, yy);
                    ctx2d.lineTo(c.w / 2, yy);
                    ctx2d.stroke();
                }

                // 4 metal corner brackets
                ctx2d.fillStyle = "#2A2422";
                const cornerL = 14, cornerT = 10;
                ctx2d.fillRect(-c.w / 2, -c.h / 2, cornerL, cornerT);
                ctx2d.fillRect(c.w / 2 - cornerL, -c.h / 2, cornerL, cornerT);
                ctx2d.fillRect(-c.w / 2, c.h / 2 - cornerT, cornerL, cornerT);
                ctx2d.fillRect(c.w / 2 - cornerL, c.h / 2 - cornerT, cornerL, cornerT);
                ctx2d.fillStyle = "#5A5450";
                [[-c.w / 2 + 3, -c.h / 2 + 3], [c.w / 2 - 6, -c.h / 2 + 3],
                 [-c.w / 2 + 3, c.h / 2 - 6], [c.w / 2 - 6, c.h / 2 - 6]].forEach(([px, py]) => {
                    ctx2d.beginPath();
                    ctx2d.arc(px, py, 1.5, 0, Math.PI * 2);
                    ctx2d.fill();
                });

                // subtle top highlight
                ctx2d.fillStyle = "rgba(255,255,255,0.14)";
                ctx2d.fillRect(-c.w / 2 + cornerL, -c.h / 2, c.w - cornerL * 2, 4);

                // Hover glow — pulsing golden outline when the player is aiming at this crate
                if (c.hovered && !c.opened) {
                    const puls = 0.55 + 0.45 * Math.sin(performance.now() / 160);
                    ctx2d.strokeStyle = `rgba(246, 210, 75, ${puls})`;
                    ctx2d.lineWidth = 5;
                    ctx2d.strokeRect(-c.w / 2 - 3, -c.h / 2 - 3, c.w + 6, c.h + 6);
                }

                // === BULLSEYE TARGET painted on the crate face ===
                // Powerup crates show a $ badge instead of a bullseye so players
                // can distinguish "bonus" crates from "answer" crates.
                if (c.powerup) {
                    ctx2d.fillStyle = "#3E2A0A";
                    ctx2d.font = `900 ${Math.round(c.h * 0.5)}px ui-sans-serif, system-ui`;
                    ctx2d.textAlign = "center";
                    ctx2d.textBaseline = "middle";
                    ctx2d.fillText("$", 0, 2);
                } else {
                    const targetR = Math.min(c.w, c.h) * 0.36;
                    const rings = [
                        { r: targetR,        color: "#B93A2E" },  // outer red
                        { r: targetR * 0.78, color: "#F5EFE0" },  // cream
                        { r: targetR * 0.56, color: "#B93A2E" },  // red
                        { r: targetR * 0.34, color: "#F5EFE0" },  // cream
                        { r: targetR * 0.16, color: "#B93A2E" },  // bullseye
                    ];
                    rings.forEach(({ r, color }) => {
                        ctx2d.fillStyle = color;
                        ctx2d.beginPath();
                        ctx2d.arc(0, 0, r, 0, Math.PI * 2);
                        ctx2d.fill();
                    });
                    // subtle target rim shadow
                    ctx2d.strokeStyle = "rgba(0,0,0,0.35)";
                    ctx2d.lineWidth = 1.5;
                    ctx2d.beginPath();
                    ctx2d.arc(0, 0, targetR, 0, Math.PI * 2);
                    ctx2d.stroke();
                }
            }
            ctx2d.restore();

            // === Modern carnival-style SIGN hanging under the crate ===
            // Chunky angled banner shape with layered wood grain, warm rim
            // highlight, cream lettering, and an independent bob so it feels
            // alive. Renders in the crate's un-shaken space so text stays crisp.
            if (!c.opened && !c.powerup) {
                const plaqueW = c.w * 1.02;
                const plaqueH = 62;
                // Independent bob — phase-offset so neighbouring signs sway differently
                const signBob = Math.sin(performance.now() / 520 + c.x * 0.03) * 2.2;
                const signTilt = Math.sin(performance.now() / 900 + c.x * 0.02) * 0.03;
                const plaqueY = cy + c.h / 2 + 20 + signBob;
                ctx2d.save();
                ctx2d.translate(cx, plaqueY);
                ctx2d.rotate(signTilt);

                // Twin ropes (with slight curve) tethering the sign to the crate above
                ctx2d.strokeStyle = "rgba(35, 20, 10, 0.75)";
                ctx2d.lineWidth = 2.5;
                ctx2d.lineCap = "round";
                [-plaqueW * 0.34, plaqueW * 0.34].forEach((rx) => {
                    ctx2d.beginPath();
                    ctx2d.moveTo(rx, -18 - signBob);
                    ctx2d.quadraticCurveTo(rx * 0.85, -10, rx, -plaqueH / 2 + 4);
                    ctx2d.stroke();
                });

                // Angled banner shape: rectangle with cut top/bottom corners →
                // the classic county-fair sign silhouette.
                const cut = 10;
                const bannerPath = new Path2D();
                bannerPath.moveTo(-plaqueW / 2 + cut, -plaqueH / 2);
                bannerPath.lineTo(plaqueW / 2 - cut, -plaqueH / 2);
                bannerPath.lineTo(plaqueW / 2, -plaqueH / 2 + cut);
                bannerPath.lineTo(plaqueW / 2, plaqueH / 2 - cut);
                bannerPath.lineTo(plaqueW / 2 - cut, plaqueH / 2);
                bannerPath.lineTo(-plaqueW / 2 + cut, plaqueH / 2);
                bannerPath.lineTo(-plaqueW / 2, plaqueH / 2 - cut);
                bannerPath.lineTo(-plaqueW / 2, -plaqueH / 2 + cut);
                bannerPath.closePath();

                // Drop shadow underneath the sign
                ctx2d.save();
                ctx2d.translate(3, 6);
                ctx2d.fillStyle = "rgba(0, 0, 0, 0.35)";
                ctx2d.fill(bannerPath);
                ctx2d.restore();

                // Wood body gradient
                const pg = ctx2d.createLinearGradient(0, -plaqueH / 2, 0, plaqueH / 2);
                pg.addColorStop(0,   "#D69A5C");
                pg.addColorStop(0.55,"#A66E33");
                pg.addColorStop(1,   "#6E3F14");
                ctx2d.fillStyle = pg;
                ctx2d.fill(bannerPath);

                // Wood grain streaks — 3 horizontal fine lines
                ctx2d.save();
                ctx2d.clip(bannerPath);
                ctx2d.strokeStyle = "rgba(0,0,0,0.12)";
                ctx2d.lineWidth = 1;
                for (let i = 0; i < 3; i++) {
                    const gy = -plaqueH / 2 + (i + 1) * (plaqueH / 4);
                    ctx2d.beginPath();
                    ctx2d.moveTo(-plaqueW / 2, gy + Math.sin(i * 12.3) * 1.5);
                    ctx2d.lineTo( plaqueW / 2, gy + Math.sin(i * 8.1) * 1.5);
                    ctx2d.stroke();
                }
                // Warm top rim highlight (sun catching the sign)
                const rim = ctx2d.createLinearGradient(0, -plaqueH / 2, 0, -plaqueH / 2 + 8);
                rim.addColorStop(0, "rgba(255, 232, 168, 0.85)");
                rim.addColorStop(1, "rgba(255, 232, 168, 0)");
                ctx2d.fillStyle = rim;
                ctx2d.fillRect(-plaqueW / 2, -plaqueH / 2, plaqueW, 8);
                // Bottom inner shadow so the sign looks 3D
                const bot = ctx2d.createLinearGradient(0, plaqueH / 2 - 8, 0, plaqueH / 2);
                bot.addColorStop(0, "rgba(0,0,0,0)");
                bot.addColorStop(1, "rgba(0,0,0,0.35)");
                ctx2d.fillStyle = bot;
                ctx2d.fillRect(-plaqueW / 2, plaqueH / 2 - 8, plaqueW, 8);
                ctx2d.restore();

                // Dark bevelled edge
                ctx2d.strokeStyle = "rgba(30, 15, 5, 0.9)";
                ctx2d.lineWidth = 2;
                ctx2d.stroke(bannerPath);

                // 4 brass rivets
                ctx2d.fillStyle = "#F3D26B";
                [[-plaqueW * 0.46, -plaqueH * 0.28], [plaqueW * 0.46, -plaqueH * 0.28],
                 [-plaqueW * 0.46,  plaqueH * 0.28], [plaqueW * 0.46,  plaqueH * 0.28]].forEach(([px, py]) => {
                    ctx2d.beginPath();
                    ctx2d.arc(px, py, 3, 0, Math.PI * 2);
                    ctx2d.fill();
                    ctx2d.fillStyle = "rgba(0,0,0,0.4)";
                    ctx2d.beginPath();
                    ctx2d.arc(px + 0.6, py + 0.6, 1.2, 0, Math.PI * 2);
                    ctx2d.fill();
                    ctx2d.fillStyle = "#F3D26B";
                });

                // === LABEL — punchy uppercase cream text with dark shadow ===
                ctx2d.textAlign = "center";
                ctx2d.textBaseline = "middle";
                const raw = String(c.text || "").toUpperCase();
                // Auto-size font: shrink when the answer is long so nothing clips.
                let fSize = Math.max(17, Math.min(26, c.w * 0.13));
                const maxLines = 4;
                let lines;
                for (let step = 0; step < 4; step++) {
                    ctx2d.font = `900 ${fSize}px "Space Grotesk", ui-sans-serif, system-ui, sans-serif`;
                    lines = wrapMax(ctx2d, raw, plaqueW - 22, maxLines);
                    // If the wrap truncated (i.e. more words remain than shown), try smaller
                    const measured = lines.reduce((n, l) => n + l.split(" ").length, 0);
                    const total = raw.split(" ").length;
                    if (measured >= total || fSize <= 13) break;
                    fSize -= 2;
                }
                const lh = fSize + 2;
                // Cast a soft dark shadow behind the text for readability
                ctx2d.fillStyle = "rgba(20, 8, 2, 0.55)";
                lines.forEach((line, i) => {
                    ctx2d.fillText(line, 1.5, -((lines.length - 1) * lh) / 2 + i * lh + 1.5);
                });
                // Cream / off-white text
                ctx2d.fillStyle = "#FFF3D9";
                lines.forEach((line, i) => {
                    ctx2d.fillText(line, 0, -((lines.length - 1) * lh) / 2 + i * lh);
                });
                ctx2d.restore();
            }

            if (c.shake > 0) c.shake = Math.max(0, c.shake - dt * 20);
        });

        // ---- water bullets (chunky rainbow stream) ----
        stateRef.current.bullets.forEach((b) => {
            b.x += b.vx * dt;
            b.y += b.vy * dt;
            // stream trail — 6 fading circles behind the bullet
            for (let k = 5; k >= 0; k--) {
                const tx = b.x - b.vx * k * 0.014;
                const ty = b.y - b.vy * k * 0.014;
                const rad = 16 - k * 2;
                const g = ctx2d.createRadialGradient(tx, ty, 1, tx, ty, rad);
                const alpha = (6 - k) / 6;
                g.addColorStop(0, `rgba(230, 250, 255, ${0.9 * alpha})`);
                g.addColorStop(0.5, `rgba(120, 210, 255, ${0.65 * alpha})`);
                g.addColorStop(1, `rgba(80, 180, 220, 0)`);
                ctx2d.fillStyle = g;
                ctx2d.beginPath();
                ctx2d.arc(tx, ty, rad, 0, Math.PI * 2);
                ctx2d.fill();
            }
            // bright core
            ctx2d.fillStyle = "rgba(255,255,255,0.98)";
            ctx2d.beginPath();
            ctx2d.arc(b.x, b.y, 5, 0, Math.PI * 2);
            ctx2d.fill();
        });

        // ---- water splash particles (from bullet impact) ----
        for (let i = stateRef.current.splashFx.length - 1; i >= 0; i--) {
            const p = stateRef.current.splashFx[i];
            p.vy += 900 * dt;   // gravity
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.life -= dt;
            if (p.life <= 0 || p.y > H + 40) {
                stateRef.current.splashFx.splice(i, 1);
                continue;
            }
            const a = Math.max(0, Math.min(1, p.life / p.maxLife));
            ctx2d.fillStyle = `rgba(180, 230, 255, ${0.85 * a})`;
            ctx2d.beginPath();
            ctx2d.arc(p.x, p.y, p.r, 0, Math.PI * 2);
            ctx2d.fill();
            // brighter core
            ctx2d.fillStyle = `rgba(255,255,255,${0.6 * a})`;
            ctx2d.beginPath();
            ctx2d.arc(p.x, p.y, p.r * 0.4, 0, Math.PI * 2);
            ctx2d.fill();
        }

        // ---- coin particle FX (from correct crate) ----
        for (let i = stateRef.current.coinsFx.length - 1; i >= 0; i--) {
            const p = stateRef.current.coinsFx[i];
            p.vy += 800 * dt;   // gravity
            p.x += p.vx * dt;
            p.y += p.vy * dt;
            p.life -= dt;
            p.rot += p.vr * dt;
            if (p.life <= 0 || p.y > H + 40) {
                stateRef.current.coinsFx.splice(i, 1);
                continue;
            }
            ctx2d.save();
            ctx2d.translate(p.x, p.y);
            ctx2d.rotate(p.rot);
            // coin body — gold gradient ellipse (rotates to look like a spinning coin)
            const cw = 16 * Math.abs(Math.cos(p.rot)) + 4;
            const cg = ctx2d.createRadialGradient(-3, -3, 2, 0, 0, 10);
            cg.addColorStop(0, "#FFF3B8");
            cg.addColorStop(0.5, "#F6D24B");
            cg.addColorStop(1, "#B8842A");
            ctx2d.fillStyle = cg;
            ctx2d.beginPath();
            ctx2d.ellipse(0, 0, cw, 12, 0, 0, Math.PI * 2);
            ctx2d.fill();
            ctx2d.strokeStyle = "#8A6316";
            ctx2d.lineWidth = 1.5;
            ctx2d.stroke();
            // $ marking (only when coin is roughly face-on)
            if (cw > 12) {
                ctx2d.fillStyle = "#7A5410";
                ctx2d.font = "bold 12px ui-sans-serif, system-ui";
                ctx2d.textAlign = "center";
                ctx2d.textBaseline = "middle";
                ctx2d.fillText("$", 0, 1);
            }
            ctx2d.restore();
        }

        // ---- HERO SPRITE at bottom-left (character stays anchored, auto-aims at clicked crate) ----
        // Position: bottom-left corner area — matches the beach reference layout.
        // Muzzle & aim are computed from the sprite's approximate gun position.
        const heroCX = Math.min(W * 0.2, 240);
        stateRef.current.heroX = heroCX / W;
        // Sprite metrics — scales with viewport height. Cap keeps it readable
        // even on huge desktop displays.
        const spriteH = Math.min(H * 0.5, 460);
        const spriteAR = 0.9;   // width / height ratio for a typical portrait-crop full-body
        const spriteW = spriteH * spriteAR;
        const spriteBottomY = H;  // hero stands on the very bottom edge
        const spriteX = heroCX - spriteW / 2;
        const spriteY = spriteBottomY - spriteH;

        // Muzzle roughly at 75% width, 35% height of the sprite frame (upper-right area
        // where the gun barrel points to). This is where bullets originate + splash comes from.
        const muzzleX = spriteX + spriteW * 0.82;
        const muzzleY = spriteY + spriteH * 0.32;
        stateRef.current.muzzle = { x: muzzleX, y: muzzleY };

        // aim vector no longer needed in draw — auto-aim is computed at fire time
        // from muzzle → clicked crate. (Left blank on purpose.)

        // Draw sprite (image already has water gun baked in)
        const sprite = heroFullRef.current;
        if (sprite && sprite.complete && sprite.naturalWidth > 0) {
            // idle animation: gentle vertical bob + a subtle side tilt
            const now = performance.now();
            const bob = Math.sin(now / 400) * 3;
            const tilt = Math.sin(now / 700) * 0.02;   // ~1.15° sway

            // Recoil animation on fire — decays over ~150ms.
            stateRef.current.recoilT = Math.max(0, (stateRef.current.recoilT || 0) - dt / 0.15);
            const recoil = stateRef.current.recoilT;   // 0..1
            // Ease-out (quad) so the kick is sharp then settles.
            const recoilEase = 1 - (1 - recoil) * (1 - recoil);
            const kickY = recoilEase * 6;              // push down (recoil into ground)
            const kickX = -recoilEase * 5;             // slight push back (left)
            const kickRot = -recoilEase * 0.08;        // slight backward rotation
            const kickScale = 1 - recoilEase * 0.03;   // tiny squash on hit

            // ground shadow
            ctx2d.fillStyle = "rgba(0,0,0,0.32)";
            ctx2d.beginPath();
            ctx2d.ellipse(heroCX, spriteBottomY - 6, spriteW * 0.35, 10, 0, 0, Math.PI * 2);
            ctx2d.fill();

            // Draw with recoil + idle tilt transform
            ctx2d.save();
            ctx2d.translate(heroCX + kickX, spriteBottomY - spriteH / 2 + bob + kickY);
            ctx2d.rotate(tilt + kickRot);
            ctx2d.scale(kickScale, kickScale);
            ctx2d.drawImage(sprite, -spriteW / 2, -spriteH / 2, spriteW, spriteH);
            ctx2d.restore();
        } else {
            // Fallback while the sprite loads — small circle so game still plays.
            ctx2d.fillStyle = "#F6D24B";
            ctx2d.beginPath();
            ctx2d.arc(heroCX, H * 0.85, 40, 0, Math.PI * 2);
            ctx2d.fill();
        }
    }, []);

    /* ---------- Line wrap helper (canvas) ---------- */
    const wrap = (ctx2d, text, maxWidth) => {
        const words = text.split(" ");
        const lines = [];
        let line = "";
        words.forEach((w) => {
            const test = line ? line + " " + w : w;
            if (ctx2d.measureText(test).width > maxWidth && line) {
                lines.push(line);
                line = w;
            } else {
                line = test;
            }
        });
        if (line) lines.push(line);
        return lines.slice(0, 3);
    };

    // Same as wrap() but caller controls max lines — used by the plaque so
    // long answers can auto-shrink font instead of getting clipped.
    const wrapMax = (ctx2d, text, maxWidth, maxLines) => {
        const words = text.split(" ");
        const lines = [];
        let line = "";
        words.forEach((w) => {
            const test = line ? line + " " + w : w;
            if (ctx2d.measureText(test).width > maxWidth && line) {
                lines.push(line);
                line = w;
            } else {
                line = test;
            }
        });
        if (line) lines.push(line);
        return lines.slice(0, maxLines);
    };

    /* ---------- Collision + loop ---------- */
    useEffect(() => {
        if (phase !== "playing") return;
        let last = performance.now();
        const tick = () => {
            const now = performance.now();
            const dt = Math.min(0.05, (now - last) / 1000);
            last = now;
            draw(dt);

            const H = stateRef.current.canvasH;
            const W = stateRef.current.canvasW;
            // bullet-crate collision
            const bs = stateRef.current.bullets;
            const cs = stateRef.current.crates;
            for (let i = bs.length - 1; i >= 0; i--) {
                const b = bs[i];
                if (b.y < -30 || b.x < -30 || b.x > W + 30 || b.y > H + 30) {
                    bs.splice(i, 1);
                    continue;
                }
                for (let j = 0; j < cs.length; j++) {
                    const c = cs[j];
                    if (c.opened) continue;
                    if (
                        b.x > c.x - c.w / 2 && b.x < c.x + c.w / 2 &&
                        b.y > c.y - c.h / 2 && b.y < c.y + c.h / 2
                    ) {
                        // hit — always spawn a splash of water droplets at impact
                        for (let k = 0; k < 16; k++) {
                            const ang = -Math.PI / 2 + (Math.random() - 0.5) * Math.PI;
                            const sp = 140 + Math.random() * 260;
                            const life = 0.55 + Math.random() * 0.35;
                            stateRef.current.splashFx.push({
                                x: b.x, y: b.y,
                                vx: Math.cos(ang) * sp,
                                vy: Math.sin(ang) * sp - 60,
                                r: 3 + Math.random() * 3,
                                life, maxLife: life,
                            });
                        }
                        c.opened = true;
                        c.shake = 8;
                        bs.splice(i, 1);
                        stateRef.current.acceptingInput = false;

                        // ==== GOLDEN POWERUP CRATE ====
                        if (c.powerup) {
                            c.correctOpen = true;
                            sfxSplash(); sfxPowerup();
                            const now = performance.now();
                            if (c.powerup === "freeze") {
                                stateRef.current.freezeUntil = now + 3500;
                                setPowerupHUD({ kind: "freeze", expiresAt: now + 3500 });
                            } else if (c.powerup === "triple") {
                                stateRef.current.tripleShotsLeft = 3;
                                setPowerupHUD({ kind: "triple", expiresAt: now + 8000 });
                            } else if (c.powerup === "life") {
                                setLives((v) => Math.min(3, v + 1));
                                setPowerupHUD({ kind: "life", expiresAt: now + 2000 });
                            }
                            setTimeout(() => { setPowerupHUD(null); stateRef.current.acceptingInput = true; }, 800);
                            break;
                        }

                        // ==== NORMAL Q&A CRATE ====
                        if (c.correct) {
                            c.correctOpen = true;
                            const perk = stateRef.current.perk;
                            const newStreak = streak + 1;
                            setStreak(newStreak);

                            // Streak bonus at ×perk.streakAt (Finn = 2, others = 3)
                            const streakBonus = newStreak >= perk.streakAt;
                            if (streakBonus) {
                                setStreakBanner(newStreak);
                                sfxStreak();
                                setTimeout(() => setStreakBanner(0), 1400);
                            }

                            const baseCoins = 50;
                            const coinsEarned = Math.round(baseCoins * perk.coinMul * (streakBonus ? 2 : 1));

                            // Spawn coin particle burst
                            const bulletCount = streakBonus ? 32 : 22;
                            for (let k = 0; k < bulletCount; k++) {
                                const ang = -Math.PI / 2 + (Math.random() - 0.5) * 1.6;
                                const sp = 200 + Math.random() * 240;
                                stateRef.current.coinsFx.push({
                                    x: c.x + (Math.random() - 0.5) * 20,
                                    y: c.y,
                                    vx: Math.cos(ang) * sp,
                                    vy: Math.sin(ang) * sp,
                                    rot: Math.random() * Math.PI * 2,
                                    vr: (Math.random() - 0.5) * 12,
                                    life: 1.6 + Math.random() * 0.6,
                                });
                            }

                            // Buck perk — splash damage smashes nearest wrong crate for bonus coins
                            if (perk.buckBonus) {
                                const target = cs.find((oc) => !oc.opened && !oc.correct && !oc.powerup);
                                if (target) {
                                    target.opened = true;
                                    target.wrongOpen = true;
                                    for (let k = 0; k < 10; k++) {
                                        stateRef.current.splashFx.push({
                                            x: target.x, y: target.y, vx: (Math.random()-0.5)*260,
                                            vy: -Math.random() * 220, r: 3 + Math.random()*3,
                                            life: 0.6, maxLife: 0.6,
                                        });
                                    }
                                }
                            }

                            sfxSplash(); sfxCoin(); sfxCashRegister();
                            setCoins((v) => v + coinsEarned);
                            setFeedback({ correct: true, why: c.why, streakBonus, coinsEarned });
                            // 8-second explanation window so the player has time to read.
                            setTimeout(() => {
                                setFeedback(null);
                                if (levelIdx + 1 >= LEVELS.length) {
                                    setPhase("win");
                                    stopAmbient();
                                } else {
                                    sfxLevelUp();
                                    setLevelIdx((v) => v + 1);
                                }
                            }, 8000);
                        } else {
                            // WRONG ANSWER — instant Game Over. No lives, no retry.
                            // Player still gets 8 seconds to read the explanation
                            // before the Game Over screen appears.
                            c.wrongOpen = true;
                            setStreak(0);
                            sfxSplash(); sfxWrong();
                            setFeedback({ correct: false, why: c.why });
                            setLives(0);
                            setTimeout(() => {
                                sfxGameOver();
                                setPhase("over");
                                stopAmbient();
                            }, 8000);
                        }
                        break;
                    }
                }
            }

            stateRef.current.rafId = requestAnimationFrame(tick);
        };
        stateRef.current.rafId = requestAnimationFrame(tick);
        return () => cancelAnimationFrame(stateRef.current.rafId);
    }, [phase, levelIdx, LEVELS.length, draw, streak]);

    /* ---------- Spawn new crates when levelIdx changes ---------- */
    useEffect(() => {
        if (phase === "playing") {
            // Trigger the level intro banner (auto-dismisses)
            setLevelBanner(true);
            const bt = setTimeout(() => setLevelBanner(false), 900);
            // wait a tick so canvas dims are ready, then spawn slightly after banner
            const st = setTimeout(() => spawnLevel(levelIdx), 400);
            return () => { clearTimeout(bt); clearTimeout(st); };
        }
    }, [levelIdx, phase, spawnLevel]);

    /* ---------- Input handlers — hover a crate, click to fire at it ---------- */
    const pointerAt = (clientX, clientY) => {
        const el = canvasRef.current;
        if (!el) return null;
        const r = el.getBoundingClientRect();
        return { x: clientX - r.left, y: clientY - r.top };
    };

    const pointerMove = (clientX, clientY) => {
        const p = pointerAt(clientX, clientY);
        if (!p) return;
        // Highlight the crate currently under the pointer (visual hover glow).
        let any = false;
        stateRef.current.crates.forEach((c) => {
            const inside = !c.opened &&
                p.x > c.x - c.w / 2 && p.x < c.x + c.w / 2 &&
                p.y > c.y - c.h / 2 && p.y < c.y + c.h / 2;
            c.hovered = inside;
            if (inside) any = true;
        });
        stateRef.current.pointer = p;
        stateRef.current.pointerOverCrate = any;
    };

    const fireAtCrate = (targetCrate) => {
        if (!stateRef.current.acceptingInput) return;
        const muzzle = stateRef.current.muzzle || { x: 0, y: 0 };
        // Auto-aim: derive the shot angle from muzzle → target crate center.
        const ang = Math.atan2(targetCrate.y - muzzle.y, targetCrate.x - muzzle.x);
        stateRef.current.gunAng = ang;
        const perk = stateRef.current.perk;
        const speed = 780 * perk.bulletMul;

        const spawnBullet = (a) => {
            stateRef.current.bullets.push({
                x: muzzle.x + Math.cos(a) * 12,
                y: muzzle.y + Math.sin(a) * 12,
                vx: Math.cos(a) * speed,
                vy: Math.sin(a) * speed,
            });
        };

        if (stateRef.current.tripleShotsLeft > 0) {
            const spread = 0.12;
            spawnBullet(ang - spread);
            spawnBullet(ang);
            spawnBullet(ang + spread);
            stateRef.current.tripleShotsLeft -= 1;
            if (stateRef.current.tripleShotsLeft === 0) setPowerupHUD(null);
        } else {
            spawnBullet(ang);
        }
        sfxCannon();
        // Trigger sprite recoil kick — decays in ~150ms in the draw loop.
        stateRef.current.recoilT = 1;

        // Small muzzle-flash splash at the gun tip so shots feel forceful.
        for (let k = 0; k < 8; k++) {
            const a = ang + (Math.random() - 0.5) * 0.6;
            const sp = 120 + Math.random() * 180;
            stateRef.current.splashFx.push({
                x: muzzle.x, y: muzzle.y,
                vx: Math.cos(a) * sp,
                vy: Math.sin(a) * sp,
                r: 2 + Math.random() * 3,
                life: 0.35, maxLife: 0.35,
            });
        }
    };

    const handleClick = (clientX, clientY) => {
        const p = pointerAt(clientX, clientY);
        if (!p) return;
        const target = stateRef.current.crates.find(
            (c) => !c.opened &&
                p.x > c.x - c.w / 2 && p.x < c.x + c.w / 2 &&
                p.y > c.y - c.h / 2 && p.y < c.y + c.h / 2
        );
        if (target) fireAtCrate(target);
    };

    /* ---------- Start / mute controls ---------- */
    const startGame = (heroId) => {
        primeAudio();
        startAmbient();
        setHero(heroId);
        setLives(1);       // one wrong shot = game over (life powerup can grant a bonus chance)
        setCoins(0);
        setLevelIdx(0);
        setStreak(0);
        setStreakBanner(0);
        setPowerupHUD(null);
        stateRef.current.heroX = 0.5;
        stateRef.current.coinsFx = [];
        stateRef.current.splashFx = [];
        stateRef.current.freezeUntil = 0;
        stateRef.current.tripleShotsLeft = 0;
        stateRef.current.perk = getPerk(heroId);
        // Fresh random question set for THIS run.
        setLevels(pickRandomLevels(12, language));
        setPhase("playing");
    };

    const toggleMute = () => {
        const nv = !muted;
        setMuted(nv);
        setMutedState(nv);
    };

    const exit = () => {
        stopAmbient();
        onExit && onExit();
    };

    const restart = () => {
        setPhase("select");
        stopAmbient();
    };

    /* ---------- Render ---------- */
    if (phase === "select") {
        return (
            <div className="w-screen h-screen overflow-y-auto p-4 md:p-8" style={{ background: "linear-gradient(180deg, #78D9F5 0%, #B7E7F7 100%)" }}>
                <div className="flex items-center justify-between mb-2">
                    <button data-testid="cashstrike-exit" onClick={exit}
                            className="text-white/95 hover:text-white bg-black/25 hover:bg-black/40 rounded-full p-2 transition">
                        <X className="w-5 h-5" />
                    </button>
                    <button data-testid="cashstrike-mute" onClick={toggleMute}
                            className="text-white/95 hover:text-white bg-black/25 hover:bg-black/40 rounded-full p-2 transition">
                        {muted ? <VolumeX className="w-5 h-5" /> : <Volume2 className="w-5 h-5" />}
                    </button>
                </div>
                <div className="text-center mt-2">
                    <h1 className="font-display font-black text-5xl md:text-7xl tracking-tight leading-none">
                        <span style={{ color: "#F6D24B", textShadow: "0 4px 0 rgba(0,0,0,0.15)" }}>CA$H</span>{" "}
                        <span className="text-white" style={{ textShadow: "0 4px 0 rgba(0,0,0,0.15)" }}>STRIKE</span>
                    </h1>
                    <div className="mt-3 inline-block bg-[#4A6E97]/50 text-white/95 rounded-full px-4 py-1.5 text-sm md:text-base">
                        {isEs ? "Dispara la respuesta correcta antes de que la deuda te alcance." : "Shoot the right answer before debt reaches you."}
                    </div>
                </div>

                <div className="max-w-4xl mx-auto mt-8 grid grid-cols-2 md:grid-cols-3 gap-4 md:gap-5">
                    {HEROES.map((h) => {
                        const perk = getPerk(h.id);
                        return (
                        <button key={h.id}
                                data-testid={`cashstrike-hero-${h.id}`}
                                onClick={() => startGame(h.id)}
                                className="group bg-white rounded-2xl overflow-hidden text-left border-4 border-[#F6D24B]/90 hover:border-[#F6D24B] transition-all hover:-translate-y-1 shadow-xl">
                            <div className="aspect-square bg-gradient-to-b from-sky-200 to-amber-100 overflow-hidden">
                                <img
                                    src={heroUrl(h.id)}
                                    alt={h.name}
                                    loading="lazy"
                                    onError={(e) => { e.currentTarget.style.display = "none"; }}
                                    className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                                />
                            </div>
                            <div className="p-3 md:p-4">
                                <div className="font-display font-black text-[#264569] text-lg md:text-2xl">
                                    {h.name.toUpperCase()}
                                </div>
                                <div className="text-slate-700 text-xs md:text-sm mt-1 leading-snug">
                                    {isEs ? h.traits_es : h.traits_en}
                                </div>
                                {/* Perk badge — makes hero choice feel meaningful */}
                                <div className="mt-2 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] md:text-xs font-black uppercase tracking-wider"
                                     style={{ background: "linear-gradient(180deg, #FFE58F, #F6D24B)", color: "#3E2A0A" }}>
                                    <Coins className="w-3 h-3" />
                                    {isEs ? perk.label_es : perk.label_en}
                                </div>
                                <div className="mt-2 md:mt-3 text-[10px] md:text-xs font-bold uppercase tracking-widest text-rose-600">
                                    {isEs ? "Elige a tu héroe" : "Choose your hero"}
                                </div>
                            </div>
                        </button>
                    ); })}
                </div>

                <div className="text-center mt-8 text-white/95">
                    <div className="inline-block bg-[#4A6E97]/50 rounded-full px-4 py-1.5 text-xs md:text-sm">
                        {isEs
                            ? "Toca una caja para disparar. La correcta te da monedas; la equivocada, fin del juego."
                            : "Tap a crate to fire. Right one pays out; wrong one is game over."}
                    </div>
                </div>
            </div>
        );
    }

    const currentQ = LEVELS[levelIdx]?.q || "";

    return (
        <div ref={containerRef} className="relative w-screen h-[100dvh] select-none overflow-hidden bg-black">
            <canvas
                ref={canvasRef}
                data-testid="cashstrike-canvas"
                className="absolute inset-0 w-full h-full touch-none block cursor-crosshair"
                onMouseMove={(e) => pointerMove(e.clientX, e.clientY)}
                onClick={(e) => handleClick(e.clientX, e.clientY)}
                onTouchStart={(e) => { const t = e.touches[0]; if (t) pointerMove(t.clientX, t.clientY); }}
                onTouchMove={(e) => { e.preventDefault(); const t = e.touches[0]; if (t) pointerMove(t.clientX, t.clientY); }}
                onTouchEnd={(e) => {
                    e.preventDefault();
                    const t = e.changedTouches && e.changedTouches[0];
                    if (t) handleClick(t.clientX, t.clientY);
                }}
            />

            {/* HUD — premium chip styles matching the reference vision board */}
            <div className="absolute inset-x-0 top-0 p-3 md:p-4 flex items-start justify-between gap-3 pointer-events-none">
                {/* Coins chip — gold pill with icon */}
                <div className="rounded-full flex items-center gap-2 font-black text-sm md:text-base pointer-events-auto shadow-xl border-2"
                     style={{
                        background: "linear-gradient(180deg, #FFE58F 0%, #F6D24B 60%, #C99A1E 100%)",
                        borderColor: "rgba(255,255,255,0.55)",
                        color: "#3E2A0A",
                        padding: "6px 14px",
                     }}>
                    <Coins className="w-4 h-4" />
                    <span data-testid="cashstrike-coins">{coins}</span>
                </div>

                <div className="flex-1 max-w-xl text-center">
                    <div className="inline-block font-black text-xs md:text-sm rounded-full px-4 py-1 tracking-widest uppercase mb-1.5 shadow-lg border-2"
                         style={{
                            background: "linear-gradient(180deg, #34D399 0%, #10B981 100%)",
                            borderColor: "rgba(255,255,255,0.55)",
                            color: "white",
                         }}>
                        {isEs ? "Nivel" : "Level"} {levelIdx + 1}
                    </div>
                    {/* Glass question banner — larger, higher-contrast copy */}
                    <div className="rounded-2xl px-5 py-3 shadow-2xl text-base md:text-2xl font-bold border leading-snug"
                         style={{
                            background: "rgba(11, 18, 32, 0.86)",
                            backdropFilter: "blur(10px)",
                            WebkitBackdropFilter: "blur(10px)",
                            borderColor: "rgba(246, 210, 75, 0.45)",
                            color: "white",
                            boxShadow: "0 12px 34px rgba(0,0,0,0.42), 0 0 0 1px rgba(255,255,255,0.05) inset",
                         }}>
                        <div className="text-[11px] md:text-xs tracking-[0.3em] uppercase font-black mb-1" style={{ color: "#F6D24B" }}>
                            {isEs ? "Coach de dinero" : "Money Coach"}
                        </div>
                        {currentQ}
                    </div>
                </div>

                <div className="flex flex-col gap-1 items-end pointer-events-auto">
                    {/* Lives chip — rose pill */}
                    <div className="rounded-full px-2.5 py-1.5 flex items-center gap-1 shadow-xl border-2"
                         style={{
                            background: "linear-gradient(180deg, #F87171 0%, #DC2626 100%)",
                            borderColor: "rgba(255,255,255,0.55)",
                         }}>
                        {Array.from({ length: Math.max(1, lives) }).map((_, i) => (
                            <Heart key={i} className="w-3.5 h-3.5 text-white fill-white" />
                        ))}
                        {lives <= 0 && (
                            <Heart className="w-3.5 h-3.5 text-white/30" />
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button onClick={toggleMute} data-testid="cashstrike-mute"
                                className="bg-black/65 hover:bg-black/85 text-white rounded-full p-2 transition shadow-md">
                            {muted ? <VolumeX className="w-4 h-4" /> : <Volume2 className="w-4 h-4" />}
                        </button>
                        <button onClick={exit} data-testid="cashstrike-exit"
                                className="bg-black/65 hover:bg-black/85 text-white rounded-full p-2 transition shadow-md">
                            <X className="w-4 h-4" />
                        </button>
                    </div>
                </div>
            </div>

            {/* Level intro banner — pops in for each new level */}
            <AnimatePresence>
                {levelBanner && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.7, y: -20 }}
                        animate={{ opacity: 1, scale: 1, y: 0 }}
                        exit={{ opacity: 0, scale: 0.9, y: -10 }}
                        transition={{ type: "spring", stiffness: 260, damping: 22 }}
                        className="absolute inset-0 grid place-items-center pointer-events-none z-20"
                    >
                        <div className="font-display font-black text-6xl md:text-8xl tracking-tight"
                             style={{
                                color: "#F6D24B",
                                textShadow: "0 6px 0 rgba(0,0,0,0.35), 0 0 40px rgba(246, 210, 75, 0.6)",
                             }}>
                            {isEs ? "NIVEL" : "LEVEL"} {levelIdx + 1}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Streak banner — pops in when reaching perk.streakAt */}
            <AnimatePresence>
                {streakBanner > 0 && (
                    <motion.div
                        initial={{ opacity: 0, scale: 0.4, rotate: -8 }}
                        animate={{ opacity: 1, scale: 1, rotate: -3 }}
                        exit={{ opacity: 0, scale: 0.7, rotate: 0 }}
                        transition={{ type: "spring", stiffness: 320, damping: 18 }}
                        className="absolute top-24 md:top-28 inset-x-0 grid place-items-center pointer-events-none z-20"
                    >
                        <div className="rounded-2xl px-6 py-3 shadow-2xl border-4"
                             style={{
                                background: "linear-gradient(180deg, #FFE58F 0%, #F6D24B 55%, #B8842A 100%)",
                                borderColor: "rgba(255,255,255,0.7)",
                                color: "#2A1A05",
                                boxShadow: "0 0 60px rgba(246,210,75,0.65)",
                             }}>
                            <div className="font-display font-black text-3xl md:text-5xl tracking-tight">
                                STREAK ×{streakBanner}
                            </div>
                            <div className="text-xs md:text-sm font-bold uppercase tracking-widest text-amber-900">
                                {isEs ? "Monedas dobles" : "Double coins"}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Powerup HUD chip — center-top under the question banner */}
            <AnimatePresence>
                {powerupHUD && (
                    <motion.div
                        initial={{ opacity: 0, y: -8 }}
                        animate={{ opacity: 1, y: 0 }}
                        exit={{ opacity: 0, y: -8 }}
                        className="absolute inset-x-0 top-28 md:top-32 grid place-items-center pointer-events-none z-10"
                    >
                        <div className="rounded-full px-4 py-1.5 text-xs md:text-sm font-black uppercase tracking-widest shadow-xl border-2 flex items-center gap-2"
                             style={{
                                background:
                                    powerupHUD.kind === "freeze" ? "linear-gradient(180deg, #93C5FD, #3B82F6)" :
                                    powerupHUD.kind === "triple" ? "linear-gradient(180deg, #F0ABFC, #C026D3)" :
                                                                   "linear-gradient(180deg, #FCA5A5, #DC2626)",
                                borderColor: "rgba(255,255,255,0.65)",
                                color: "white",
                             }}>
                            <Sparkles className="w-3.5 h-3.5" />
                            {powerupLabel(powerupHUD.kind, isEs)}
                            {powerupHUD.kind === "triple" && stateRef.current.tripleShotsLeft > 0
                                ? ` ×${stateRef.current.tripleShotsLeft}` : ""}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Feedback marquee — wooden panel that stays 8 seconds so the player
                can actually read the explanation. Green wood + coins on correct,
                red weathered wood on wrong (game-over incoming). */}
            <AnimatePresence>
                {feedback && (
                    <motion.div
                        initial={{ opacity: 0, y: 24, scale: 0.94 }}
                        animate={{ opacity: 1, y: 0, scale: 1 }}
                        exit={{ opacity: 0, y: 24, scale: 0.95 }}
                        transition={{ type: "spring", stiffness: 220, damping: 24 }}
                        className="absolute inset-x-3 md:inset-x-1/4 bottom-4 md:bottom-8 z-30"
                        data-testid="cashstrike-feedback"
                    >
                        <div
                            className="rounded-3xl px-5 py-4 md:px-8 md:py-5 shadow-2xl border-4"
                            style={{
                                background: feedback.correct
                                    ? "linear-gradient(180deg, #10B981 0%, #047857 100%)"
                                    : "linear-gradient(180deg, #B23A2E 0%, #7A2318 100%)",
                                borderColor: feedback.correct ? "#F6D24B" : "#F3B3A6",
                                boxShadow: feedback.correct
                                    ? "0 20px 60px rgba(16,185,129,0.55), 0 0 0 1px rgba(255,255,255,0.08) inset"
                                    : "0 20px 60px rgba(178,58,46,0.55), 0 0 0 1px rgba(255,255,255,0.08) inset",
                            }}
                        >
                            <div className="flex items-center justify-between mb-1.5">
                                <div className="font-display font-black text-xl md:text-3xl tracking-tight text-white">
                                    {feedback.correct
                                        ? (isEs ? "¡CORRECTO!" : "CORRECT!")
                                        : (isEs ? "¡FALLASTE!" : "WRONG SHOT!")}
                                </div>
                                <div className="text-white/85 text-[10px] md:text-xs font-black uppercase tracking-widest">
                                    {feedback.correct
                                        ? `+${feedback.coinsEarned ?? 50}` + (isEs ? " monedas" : " coins")
                                        : (isEs ? "Fin del juego" : "Game over")}
                                </div>
                            </div>
                            <div className="text-white text-sm md:text-lg leading-snug font-medium">
                                {feedback.why}
                            </div>
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>

            {/* Game over / Win overlays */}
            {(phase === "over" || phase === "win") && (
                <div className="absolute inset-0 grid place-items-center bg-black/70 p-6">
                    <div className="bg-white rounded-3xl p-6 md:p-8 text-center max-w-md border-4 border-[#F6D24B] shadow-2xl">
                        <div className="font-display font-black text-3xl md:text-4xl text-[#264569]">
                            {phase === "win"
                                ? (isEs ? "¡Ganaste!" : "You won!")
                                : (isEs ? "¡SIN DINERO!" : "NO MONEY!")}
                        </div>
                        {phase === "over" && (
                            <div className="mt-2 text-xs md:text-sm uppercase tracking-widest text-rose-600 font-bold">
                                {isEs ? "Disparo equivocado · Fin del juego" : "Wrong shot · Game over"}
                            </div>
                        )}
                        <div className="mt-3 text-slate-700">
                            {isEs ? "Monedas ganadas" : "Coins earned"}: <span className="font-black text-amber-600">{coins}</span>
                        </div>
                        <div className="mt-6 flex gap-3 justify-center">
                            <button onClick={restart} data-testid="cashstrike-restart"
                                    className="rounded-full bg-[#F6D24B] hover:bg-[#EFC134] text-[#264569] font-black px-5 py-2.5 shadow-md">
                                {isEs ? "Jugar de nuevo" : "Play again"}
                            </button>
                            <button onClick={exit}
                                    className="rounded-full bg-slate-200 hover:bg-slate-300 text-slate-800 font-bold px-5 py-2.5">
                                {isEs ? "Salir" : "Exit"}
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default CashStrike;
